import { useQuery } from '@tanstack/react-query'

type PacksAccess = {
    allowed: boolean
    reason: string | null
}

/**
 * Whether this account may open a pack, and the sentence to show when it may not.
 *
 * The packs counterpart of `useStoreGate`, and the same argument: a freeze, a store block, the
 * trust gate and the integrity gate each refuse a pack purchase server-side, and none of them was
 * visible to the client. Packs at least *reported* the refusal -- `request()` has always checked
 * `response.ok` -- but it arrived as a dead error after the charge attempt rather than as a reason
 * the button was never live in the first place.
 *
 * `/api/packs/access` calls the same `refusal_reason` the open endpoint raises on.
 */
export const usePacksGate = () => {
    const { data } = useQuery<PacksAccess>({
        queryKey: ['packs-access'],
        queryFn: async () => {
            const response = await fetch('/api/packs/access/')
            if (!response.ok) {
                // Fail open, as the gates themselves do. The purchase stays gated regardless.
                return { allowed: true, reason: null }
            }
            return await response.json()
        },
        retry: false,
        staleTime: 1000 * 60,
    })

    return { blockedReason: data?.reason ?? undefined }
}
